import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Mail } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/data/content";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur supports-[backdrop-filter]:bg-[#020606]/85 bg-[#020606]/95 shadow-[0_8px_30px_rgba(2,6,6,0.35)]" : "bg-[#020606]/90"
      }`}
      style={{ borderBottom: "1px solid rgba(58,187,176,0.22)" }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 sm:h-[74px] items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0">
            <img src={COMPANY.logo} alt="Prince Trading Impex logo" className="h-10 w-10 sm:h-12 sm:w-12 object-contain" />
            <span className="font-serif text-lg sm:text-xl leading-none text-white">
              Prince Trading <span className="gold-text-gradient">Impex</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav data-testid="header-nav" className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `relative font-sans text-sm font-medium tracking-wide transition-colors ${
                    isActive ? "text-[#41E19A]" : "text-white/85 hover:text-[#41E19A]"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="inline-flex flex-col items-center">
                    {l.label}
                    <span
                      className="mt-1 h-px transition-all duration-300"
                      style={{ width: isActive ? "100%" : "0%", background: "linear-gradient(90deg,#3ABBB0,#41E19A)" }}
                    />
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* CTA + mobile toggle */}
          <div className="flex items-center gap-3">
            <a
              href={`mailto:${COMPANY.email}`}
              data-testid="header-email-cta"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 font-sans text-sm font-semibold transition-all"
              style={{ background: "linear-gradient(90deg,#3ABBB0,#41E19A)", color: "#020606", boxShadow: "0 6px 20px rgba(58,187,176,0.35)" }}
              onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.08)")}
              onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
            >
              Email Us <Mail size={16} />
            </a>
            <button
              data-testid="header-mobile-menu-button"
              aria-label="Toggle menu"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg text-white"
              style={{ border: "1px solid rgba(58,187,176,0.35)" }}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${open ? "max-h-[80vh]" : "max-h-0"}`}
        style={{ background: "#020606", borderTop: open ? "1px solid rgba(58,187,176,0.18)" : "none" }}
      >
        <nav className="px-4 sm:px-6 py-4 flex flex-col">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `py-3 font-sans text-base border-b border-white/5 ${isActive ? "text-gold" : "text-white/80"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={`mailto:${COMPANY.email}`}
            className="mt-5 inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-3 font-sans text-sm font-semibold"
            style={{ background: "linear-gradient(90deg,#3ABBB0,#41E19A)", color: "#020606" }}
          >
            Email Us <Mail size={16} />
          </a>
        </nav>
      </div>
    </header>
  );
};

export default Header;
